<template>
  <section>
    <Paragraph class="font-jost-regular text-blue-dark text-[25px] leading-[30px] mt-10" text="Verification" />
    <div class="t-results text-lg mt-5">
      <section class="t-results-body grid grid-cols-2 gap-[90px] bg-white">
        <div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Block Number
            </div>
            <router-link v-if="transactionData && transactionData.block !== null"
              class="col block px-2.5 flex relative text-blue-light cursor-pointer text-[16px]"
              :to="{ name: 'block', params: { chainNumber: transactionData.chainNumber || $route.params.chainNumber || $route.params.chain, blockNumber: transactionData.block } }">
              #{{ transactionData.block }}
            </router-link>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Chain Number
            </div>
            <router-link
              v-if="transactionData.chainNumber !== undefined && transactionData.chainNumber !== null"
              class="col text-blue-light cursor-pointer text-[16px]"
              :to="{ name: 'chain', params: { chainNumber: transactionData.chainNumber } }"
            >
              #{{ transactionData.chainNumber }}
            </router-link>
            <div v-else class="col text-[16px]">
              {{ transactionData.chainNumber }}
            </div>
          </div>

           <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Coin amount
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <p class="block relative text-ellipsis text-blue-dark text-[16px]">
                {{ getBalance(coinValue.value) }} <span class="uppercase">GEEQ</span>
              </p>
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Transaction Fee
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark text-[16px]">
              {{ getBalance(transactionData.fee) }} <span class="uppercase">GEEQ</span>
            </div>
          </div>
          <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 border-b border-[#2699FB] result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Attestation Hash
            </div>
            <div class="col flex items-center font-jost-regular px-2.5 text-blue-dark text-[16px]">
              <p class="block text-ellipsis text-[16px]">
                <span class="text-blue-dark">
                  {{ attestHash }}
                </span>
              </p>

              <CopyIcon :isActive="checkIfIsEqual(attestHash, 'attestHash')" @click="copy(attestHash, 'attestHash')" title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline border-b border-[#2699FB]">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              From
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <router-link v-if="transactionData.from" class="block text-ellipsis text-[16px]"
                :to="{ name: 'account', params: { chainNumber: transactionData.chainNumber || $route.params.chainNumber || $route.params.chain, address: transactionData.from } }">
                <span class="text-blue-light">
                  {{ transactionData.from }}
                </span>
              </router-link>
              <CopyIcon :isActive="checkIfIsEqual(transactionData.from, 'sender')" @click="copy(transactionData.from, 'sender')" title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>

          <div v-if="metadata && metadata.length > 0"
            class="first__col__last__row row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Payload Metadata
            </div>
            <div class="col flex-col px-2.5 text-blue-dark gap-[10px]">
              <p class="block font-jost-regular text-ellipsis break-all text-[16px]" v-for="(i, ind) in metadata" :key="ind">
                {{ i }}
              </p>
            </div>
          </div>
        </div>

        <div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Status
            </div>
            <div class="flex items-center gap-3 col font-jost-regular px-2.5">
              <span class="px-2 py-1 text-base font-medium rounded uppercase" 
                  :class="transactionData.status === 'valid' ? 'bg-green-100 text-green-800' : 
                    transactionData.status === 'invalid' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'">
                {{ transactionData.status || 'PENDING' }}
              </span>
            </div>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Signature
            </div>
            <div class="col flex items-center font-jost-regular px-2.5 text-[16px]">
              <p v-if="transactionData" class="block text-ellipsis text-[16px]">
                <span class="text-blue-dark">
                  {{ transactionData.signature }}
                </span>
              </p>
              <CopyIcon :isActive="checkIfIsEqual(transactionData.signature, 'signature')" @click="copy(transactionData.signature, 'signature')"
                title="Copy hash" class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Timestamp
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark text-[16px]">
              {{ getTxCreationDate() }}
            </div>
          </div>
        </div>
      </section>
    </div>
  </section>

</template>

<script>
import moment from 'moment';
import string from '@/helpers/string';

import CopyIcon from "@/components/svg/CopyIcon.vue";
import Paragraph from "@/components/web/ParagraphComponent";

export default {
  name: 'AttestOverview',
  components: {
    CopyIcon,
    Paragraph
  },
  props: {
    transactionData: {
      type: Object,
      required: true
    },
    transactionRefHash: {
      type: String,
      required: true
    },
  },
  computed: {
    values() {
      const values = this.transactionData.values;
      if (Array.isArray(values)) {
        return values;
      }

      if (this.transactionData.value && typeof this.transactionData.value === 'object') {
        return [this.transactionData.value];
      }

      return [];
    },
    attestHash() {
      return this.transactionData?.payload?.attestationHash || '';
    },
    metadata() {
      return this.transactionData?.payload?.metadata || [];
    },
    sender() {
      const sender = this.transactionData?.uat?.overview?.sender;
      return sender || '';
    },
    createdAt() {
      const createdAt = this.transactionData?.uat?.overview?.createdAt || this.transactionData?.createdAt;
      return createdAt || '';
    },
    coinValue() {
      const coinValue = this.values.find((item) => item && item.type === 'coin');
      return coinValue || { value: '', type: 'coin' };
    },
  },
  data() {
    return {
      copied: '',
      balanceDivisor: parseInt(process.env.VUE_APP_BALANCE_DIVISOR, 10) || 1000000000,
    };
  },
  methods: {
    getBalance(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return amount;
      }
      const parsedAmount = Number(amount);
      if (Number.isNaN(parsedAmount) || !this.balanceDivisor) {
        return amount;
      }
      return parsedAmount / this.balanceDivisor;
    },
    copy(hash, uId = 0) {
      string.copyToClipboard(hash);
      this.setCopied(hash + uId);
    },
    setCopied(hash) {
      this.copied = hash;
    },
    checkIfIsEqual(str, uId = 0) {
      return string.isTextEqualTo(str, this.copied, uId);
    },
    getTxCreationDate() {
      const createdAt = this.createdAt;
      if (!createdAt) {
        return '';
      }
      return moment(createdAt).format('YYYY-MM-DD HH:mm:ss');
    },
  }
}
</script>

<style>
.transaction-explorer .vjs-value {
  color: #2699FB !important;
  font-size: 18px;
  line-height: 26px;
  font-family: 'Jost-Regular', monospace !important;
}

.transaction-explorer .vjs-tree {
  font-size: 18px;
  line-height: 26px;
  overflow-x: auto !important;
  white-space: nowrap;
  color: #2699FB !important;
}

.transaction-explorer .vjs-key {
  font-family: 'Jost-Regular', monospace !important;
  color: #2699FB !important;
}
</style>
<style scoped>
.col {
  flex: 1;
  /* distributes space on the line equally among items */
  width: calc(100% / 2);
}

.t-results-body {
  padding: 28px 21px;
  border-radius: 8px;
}

.t-results-body .row:first-child {
  border-top: unset;
}

@media (max-width: 992px) {
  .t-results-body {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .first__col__last__row {
    border-bottom: 1px solid #2699FB;
  }
}
</style>
